import { HexMarker, HexPattern } from '../brand/Hexagon';
import { Reveal } from '../ui/Reveal';
import { Eyebrow, SectionTitle } from '../ui/SectionHeading';
import { features } from '../../content/site';
import type { Feature } from '../../content/site';

const SURFACE: Record<Feature['variant'], string> = {
  dark: 'bg-ink-deep text-white',
  outline: 'border border-line bg-white hover:border-line-hover',
  muted: 'bg-surface-alt',
};

function FeatureCard({ feature }: { feature: Feature }) {
  const dark = feature.variant === 'dark';

  return (
    <article
      // h-full: el wrapper de Reveal es el flex item y se estira por
      // align-items: stretch; sin esto las cards de una misma fila quedan con
      // alturas distintas según su texto.
      className={`relative h-full overflow-hidden rounded-card p-[clamp(30px,3.2vw,44px)] transition-[transform,box-shadow,border-color] duration-350 hover:-translate-y-1 hover:shadow-card-white ${SURFACE[feature.variant]}`}
    >
      {dark && (
        <div className="pointer-events-none absolute inset-0 text-brand-light" aria-hidden="true">
          <HexPattern className="h-full w-full" opacity={0.1} />
        </div>
      )}

      <div className="relative">
        <HexMarker
          number={feature.number}
          className={`h-[50px] w-11 ${dark ? 'text-brand-light' : 'text-brand'}`}
        />
        <h3
          className={`mt-5 font-display text-xl font-semibold ${
            dark ? 'text-white' : 'text-ink'
          }`}
        >
          {feature.title}
        </h3>
        <p
          className={`mt-3 text-[15px] leading-[1.7] ${
            dark ? 'text-ondark' : 'text-ink-muted'
          }`}
        >
          {feature.description}
        </p>
      </div>
    </article>
  );
}

export function Features() {
  return (
    <section id="beneficios" className="section-y" aria-labelledby="beneficios-titulo">
      <div className="shell">
        <Reveal>
          <Eyebrow>{features.eyebrow}</Eyebrow>
          <SectionTitle className="mt-6 max-w-[820px]">
            <span id="beneficios-titulo">{features.title}</span>
          </SectionTitle>
        </Reveal>

        {/*
         * Bento asimétrico: el ancho lo fija flex-basis por card, no una grilla
         * rígida, así que refluye solo.
         *
         * Las bases son 536px/396px y no los 440px/300px del prototipo porque
         * aquél no declaraba `box-sizing: border-box` — sus bases excluían el
         * padding de ~48px por lado. Tailwind sí lo declara (preflight), de modo
         * que copiar 440/300 tal cual metía tres cards en la primera fila en vez
         * de dos. 536 = 440 + 96 y 396 = 300 + 96 restauran el wrap 2+2 original.
         */}
        <div className="mt-14 flex flex-wrap gap-6">
          {features.items.map((feature, index) => (
            <Reveal
              key={feature.number}
              delay={(index % 3) as 0 | 1 | 2}
              className={feature.wide ? 'grow-2 basis-[536px]' : 'grow basis-[396px]'}
            >
              <FeatureCard feature={feature} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
